import type { RefObject } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

export type ProgressDrag = {
  progressBarRef: RefObject<HTMLDivElement>;
  dragPreviewTime: number | null;
  handleProgressClick: (event: MouseEvent) => void;
  handleMouseDown: (event: MouseEvent) => void;
  handleTouchStart: (event: TouchEvent) => void;
};

const timeAt = (bar: HTMLDivElement | null, clientX: number, duration: number): number | null => {
  if (!bar) {
    return null;
  }
  const bounds = bar.getBoundingClientRect();
  const x = Math.max(0, Math.min(clientX - bounds.left, bounds.width));
  return (x / bounds.width) * duration;
};

type Drag = { dragging: { current: boolean }; preview: (clientX: number) => void; finish: (clientX: number) => void };

/** Follows the pointer and the finger across the whole document while a drag is on. */
const useDragListeners = ({ dragging, preview, finish }: Drag): void => {
  useEffect(() => {
    const move = (event: MouseEvent) => preview(event.clientX);
    const up = (event: MouseEvent) => dragging.current && finish(event.clientX);
    const touchMove = (event: TouchEvent) => {
      if (dragging.current) {
        event.preventDefault();
        preview(event.touches[0]?.clientX ?? 0);
      }
    };
    const touchEnd = (event: TouchEvent) => dragging.current && finish(event.changedTouches[0]?.clientX ?? 0);
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", touchMove, { passive: false });
    document.addEventListener("touchend", touchEnd);
    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", touchMove);
      document.removeEventListener("touchend", touchEnd);
    };
  }, []);
};

const useDragControls = (duration: number, seek: (time: number) => void) => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [dragPreviewTime, setPreview] = useState<number | null>(null);
  const live = useRef({ duration, seek });
  live.current = { duration, seek };
  const timeFor = (clientX: number) => timeAt(progressBarRef.current, clientX, live.current.duration);

  const preview = (clientX: number) => {
    const time = timeFor(clientX);
    if (time !== null && dragging.current) {
      setPreview(time);
    }
  };
  const finish = (clientX: number) => {
    const time = timeFor(clientX);
    if (dragging.current && time !== null) {
      live.current.seek(time);
    }
    dragging.current = false;
    setPreview(null);
  };
  return { progressBarRef, dragging, dragPreviewTime, timeFor, preview, finish };
};

/** Click and drag (mouse or finger) on the progress bar: while dragging the preview time shows, on release the player seeks. */
export const useProgressDrag = (duration: number, seek: (time: number) => void): ProgressDrag => {
  const { progressBarRef, dragging, dragPreviewTime, timeFor, preview, finish } = useDragControls(duration, seek);
  useDragListeners({ dragging, preview, finish });

  const begin = (clientX: number) => {
    dragging.current = true;
    preview(clientX);
  };

  return {
    progressBarRef,
    dragPreviewTime,
    handleProgressClick: (event) => {
      const time = dragging.current ? null : timeFor(event.clientX);
      if (time !== null) {
        seek(time);
      }
    },
    handleMouseDown: (event) => begin(event.clientX),
    handleTouchStart: (event) => begin(event.touches[0]?.clientX ?? 0),
  };
};
